import React, { useRef, useState, useEffect } from 'react';

/**
 * Enhanced input component that maintains its own state while focused and only
 * updates parent on blur or Enter key press. Includes improved focus management.
 */
export const UnitInput = ({
  value,
  onChange,
  placeholder,
  type = "number",
  min,
  max,
  step = "any",
  id,
  name
}) => {
  const inputRef = useRef(null);
  const [localValue, setLocalValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const inputId = id || name; // Use either id or name as unique identifier
  
  // Track if we're in the middle of an edit
  const isEditingRef = useRef(false);

  // Update local value when prop changes, but only if not focused and not editing
  useEffect(() => {
    if (!isFocused && !isEditingRef.current) {
      setLocalValue(value);
    }
  }, [value, isFocused]);

  // Handle local changes without propagating to parent
  const handleChange = (e) => {
    isEditingRef.current = true;
    setLocalValue(e.target.value);
  };

  // Handle focus event
  const handleFocus = (e) => {
    setIsFocused(true);
    // Select all text when focused for easier editing
    e.target.select();
  };

  // Update parent state when input loses focus
  const handleBlur = (e) => {
    setIsFocused(false);
    
    // Only update parent if the value actually changed
    if (localValue !== value) {
      onChange(localValue);
    }
    
    // Reset editing state after a short delay
    setTimeout(() => {
      isEditingRef.current = false;
    }, 100);
  };

  // Handle keydown events
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      // Update parent state
      if (localValue !== value) {
        onChange(localValue);
      }
      
      // Find the next input to focus
      const form = e.target.form;
      if (form) {
        const inputs = Array.from(form.querySelectorAll('input:not([disabled]), select:not([disabled]), button:not([disabled])'));
        const currentIndex = inputs.indexOf(e.target);
        const nextInput = inputs[currentIndex + 1];
        
        if (nextInput) {
          nextInput.focus();
        }
      }
      
      e.preventDefault(); // Prevent form submission
    }
  };

  return (
    <input
      ref={inputRef}
      id={id}
      name={name}
      type={type}
      value={localValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={`text-xs p-1 border border-gray-300 rounded w-full ${isFocused ? 'border-blue-500 ring-1 ring-blue-200' : ''}`}
      placeholder={placeholder}
      min={min}
      max={max}
      step={step}
      tabIndex={0} // Ensure the input is tabbable
    />
  );
};